import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Download, Copy, Share2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// ─── Canvas constants ───────────────────────────────────────────────────────
const W = 1080;
const H = 1920;
const PAD = 76;

// ─── Helpers ────────────────────────────────────────────────────────────────
function parseCSV(str) {
  return String(str || '').split(',').map(v => v.trim()).filter(Boolean);
}

function loadImg(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}

function rr(ctx, x, y, w, h, r) {
  const [tl, tr, br, bl] = typeof r === 'number' ? [r, r, r, r] : r;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
  ctx.lineTo(x + bl, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
  ctx.lineTo(x, y + tl);
  ctx.quadraticCurveTo(x, y, x + tl, y);
  ctx.closePath();
}

function clip(ctx, text, maxW) {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1);
  return t + '…';
}

function fmtDate(str) {
  if (!str) return '';
  try {
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    const d = new Date(str + 'T12:00:00');
    const s = d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch { return str; }
}

// ─── Canvas drawing ──────────────────────────────────────────────────────────
async function buildCanvas(session) {
  await document.fonts.ready;

  const t = i18n.t.bind(i18n);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#08100a');
  bg.addColorStop(0.4, '#09090b');
  bg.addColorStop(1, '#060c07');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Central radial glow
  const g1 = ctx.createRadialGradient(W / 2, H * 0.32, 60, W / 2, H * 0.32, W * 0.85);
  g1.addColorStop(0, 'rgba(34,197,94,0.15)');
  g1.addColorStop(0.55, 'rgba(34,197,94,0.04)');
  g1.addColorStop(1, 'rgba(34,197,94,0)');
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);

  // Bottom glow
  const g2 = ctx.createRadialGradient(W / 2, H, 0, W / 2, H, W * 0.55);
  g2.addColorStop(0, 'rgba(34,197,94,0.09)');
  g2.addColorStop(1, 'rgba(34,197,94,0)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  // Dot grid
  ctx.fillStyle = 'rgba(34,197,94,0.065)';
  const DOT = 54;
  for (let x = DOT; x < W; x += DOT)
    for (let y = DOT; y < H; y += DOT) {
      ctx.beginPath(); ctx.arc(x, y, 1.8, 0, Math.PI * 2); ctx.fill();
    }

  // Top vignette
  const tv = ctx.createLinearGradient(0, 0, 0, 200);
  tv.addColorStop(0, 'rgba(0,0,0,0.55)'); tv.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = tv; ctx.fillRect(0, 0, W, 200);

  // Bottom vignette
  const bv = ctx.createLinearGradient(0, H - 240, 0, H);
  bv.addColorStop(0, 'rgba(0,0,0,0)'); bv.addColorStop(1, 'rgba(0,0,0,0.65)');
  ctx.fillStyle = bv; ctx.fillRect(0, H - 240, W, 240);

  // ── Logo + brand ──────────────────────────────────────────────────────────
  const LOGO_SZ = 88;
  const LOGO_Y = 76;

  try {
    const logo = await loadImg('/dragon-logo.png');

    const halo = ctx.createRadialGradient(
      PAD + LOGO_SZ / 2, LOGO_Y + LOGO_SZ / 2, 0,
      PAD + LOGO_SZ / 2, LOGO_Y + LOGO_SZ / 2, LOGO_SZ * 0.9
    );
    halo.addColorStop(0, 'rgba(34,197,94,0.35)');
    halo.addColorStop(1, 'rgba(34,197,94,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(PAD - LOGO_SZ * 0.4, LOGO_Y - LOGO_SZ * 0.4, LOGO_SZ * 1.8, LOGO_SZ * 1.8);

    ctx.drawImage(logo, PAD, LOGO_Y, LOGO_SZ, LOGO_SZ);
  } catch { /* logo missing, skip */ }

  // App name
  ctx.font = `800 66px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#f4f4f5';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('DRAGONFIT', PAD + LOGO_SZ + 22, LOGO_Y + LOGO_SZ / 2);

  // ── Top separator ─────────────────────────────────────────────────────────
  const SEP1_Y = LOGO_Y + LOGO_SZ + 40;
  ctx.strokeStyle = 'rgba(34,197,94,0.45)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(PAD, SEP1_Y); ctx.lineTo(W - PAD, SEP1_Y); ctx.stroke();

  ctx.fillStyle = '#22c55e';
  ctx.beginPath(); ctx.arc(PAD, SEP1_Y, 5, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(W - PAD, SEP1_Y, 5, 0, Math.PI * 2); ctx.fill();

  // ── Session header card ───────────────────────────────────────────────────
  const CX = PAD;
  const CY = SEP1_Y + 36;
  const CW = W - PAD * 2;
  const CH = 256;

  ctx.fillStyle = 'rgba(12, 20, 14, 0.88)';
  rr(ctx, CX, CY, CW, CH, 28); ctx.fill();

  ctx.strokeStyle = 'rgba(34,197,94,0.22)';
  ctx.lineWidth = 1.5;
  rr(ctx, CX, CY, CW, CH, 28); ctx.stroke();

  ctx.fillStyle = '#22c55e';
  rr(ctx, CX, CY, 10, CH, [28, 0, 0, 28]); ctx.fill();

  ctx.textBaseline = 'top';

  ctx.font = `800 92px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#22c55e';
  ctx.fillText(clip(ctx, (session.day_name || 'SESSION').toUpperCase(), CW - 100), CX + 52, CY + 24);

  ctx.font = `500 34px "Manrope", "Helvetica Neue", sans-serif`;
  ctx.fillStyle = '#71717a';
  ctx.fillText(fmtDate(session.date), CX + 52, CY + 138);

  ctx.font = `700 46px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#e4e4e7';
  ctx.fillText(clip(ctx, (session.workout_name || '').toUpperCase(), CW - 100), CX + 52, CY + 188);

  // ── Exercise list ─────────────────────────────────────────────────────────
  const exAll = session.exercises || [];
  const MAX_SHOW = 10;
  const shown = exAll.slice(0, MAX_SHOW);

  // Pre-compute per-exercise stats from CSV data
  const exStats = shown.map(ex => {
    const weights  = parseCSV(ex.weight).map(Number).filter(n => !isNaN(n) && n > 0);
    const repsList = parseCSV(ex.reps).map(Number).filter(n => !isNaN(n) && n > 0);
    const setCount = Math.max(
      parseCSV(ex.weight).length,
      parseCSV(ex.reps).length
    );
    const maxWeight = weights.length > 0 ? Math.max(...weights) : null;
    const totalReps = repsList.reduce((a, b) => a + b, 0);
    return { maxWeight, totalReps, setCount };
  });

  const COL_NAME = CW * 0.50;
  const COL_PESO = CW * 0.25;
  const COL_REPS = CW * 0.25;

  const LABEL_Y = CY + CH + 52;
  ctx.font = `700 28px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#3f3f46';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(t('session_share.canvas_exercises', { count: exAll.length }), PAD, LABEL_Y);

  const HDR_Y = LABEL_Y + 46;
  ctx.font = `600 24px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#52525b';
  ctx.fillText(t('session_share.col_exercise', { defaultValue: 'EXERCISE' }).toUpperCase(), PAD + 22, HDR_Y);
  ctx.textAlign = 'center';
  ctx.fillText(t('session_share.canvas_weight'), PAD + COL_NAME + COL_PESO / 2, HDR_Y);
  ctx.fillText(t('session_share.canvas_reps'), PAD + COL_NAME + COL_PESO + COL_REPS / 2, HDR_Y);
  ctx.textAlign = 'left';

  ctx.strokeStyle = 'rgba(63,63,70,0.6)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(PAD, HDR_Y + 34); ctx.lineTo(W - PAD, HDR_Y + 34); ctx.stroke();

  const ROW_H = 114;
  const ROW_START = HDR_Y + 46;

  shown.forEach((ex, i) => {
    const ry = ROW_START + i * ROW_H;
    const even = i % 2 === 0;
    const { maxWeight, totalReps, setCount } = exStats[i];

    ctx.fillStyle = even ? 'rgba(18,26,20,0.72)' : 'rgba(14,14,18,0.5)';
    rr(ctx, PAD, ry, CW, ROW_H - 10, 18); ctx.fill();

    if (even) {
      ctx.fillStyle = 'rgba(34,197,94,0.18)';
      rr(ctx, PAD, ry, 4, ROW_H - 10, [18, 0, 0, 18]); ctx.fill();
    }

    const MID = ry + (ROW_H - 10) / 2;

    // Exercise name
    ctx.font = `600 42px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
    ctx.fillStyle = '#f4f4f5';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(clip(ctx, ex.exercise_name || '', COL_NAME - 56), PAD + 22, MID - 10);

    // Sets count below name
    if (setCount > 0) {
      ctx.font = `500 24px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
      ctx.fillStyle = '#52525b';
      ctx.fillText(`${setCount} series`, PAD + 22, MID + 18);
    }

    // Best weight (max across sets)
    ctx.textAlign = 'center';
    if (maxWeight !== null) {
      ctx.font = `800 46px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
      ctx.fillStyle = '#22c55e';
      ctx.fillText(String(maxWeight), PAD + COL_NAME + COL_PESO / 2, MID - 8);
      ctx.font = `500 22px "Manrope", "Helvetica Neue", sans-serif`;
      ctx.fillStyle = '#52525b';
      ctx.fillText('kg', PAD + COL_NAME + COL_PESO / 2, MID + 24);
    } else {
      ctx.font = `700 46px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
      ctx.fillStyle = '#52525b';
      ctx.fillText('—', PAD + COL_NAME + COL_PESO / 2, MID);
    }

    // Total reps
    ctx.font = `700 46px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
    ctx.fillStyle = '#e4e4e7';
    ctx.fillText(totalReps > 0 ? String(totalReps) : '—', PAD + COL_NAME + COL_PESO + COL_REPS / 2, MID - 8);
    if (totalReps > 0) {
      ctx.font = `500 22px "Manrope", "Helvetica Neue", sans-serif`;
      ctx.fillStyle = '#52525b';
      ctx.fillText('reps', PAD + COL_NAME + COL_PESO + COL_REPS / 2, MID + 24);
    }
    ctx.textAlign = 'left';
  });

  if (exAll.length > MAX_SHOW) {
    const moreY = ROW_START + MAX_SHOW * ROW_H;
    ctx.font = `500 32px "Manrope", "Helvetica Neue", sans-serif`;
    ctx.fillStyle = '#52525b';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('session_share.canvas_more', { count: exAll.length - MAX_SHOW }), W / 2, moreY + 32);
    ctx.textAlign = 'left';
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  const FY = H - 148;
  ctx.strokeStyle = 'rgba(34,197,94,0.3)';
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(PAD, FY); ctx.lineTo(W - PAD, FY); ctx.stroke();

  ctx.fillStyle = '#22c55e';
  ctx.beginPath(); ctx.arc(PAD, FY, 4, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(W - PAD, FY, 4, 0, Math.PI * 2); ctx.fill();

  ctx.font = `700 34px "Barlow Condensed", "Arial Narrow", "Impact", sans-serif`;
  ctx.fillStyle = '#3f3f46';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(t('session_share.canvas_header'), W / 2, FY + 56);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  return canvas;
}

// ─── Component ───────────────────────────────────────────────────────────────
const SessionShareModal = ({ session, onClose }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [generating, setGenerating] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    buildCanvas(session).then(canvas => {
      if (!active) return;
      canvasRef.current = canvas;
      setImgUrl(canvas.toDataURL('image/png'));
      setGenerating(false);
    }).catch(err => {
      console.error('Share image error:', err);
      setGenerating(false);
    });
    return () => { active = false; };
  }, [session]);

  const PREVIEW_W = Math.min(220, window.innerWidth - 80);
  const PREVIEW_H = Math.round(PREVIEW_W * (H / W));

  const getBlob = useCallback(() =>
    new Promise(res => canvasRef.current?.toBlob(res, 'image/png')), []);

  const handleDownload = useCallback(async () => {
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dragonfit-${(session.day_name || 'session').replace(/\s+/g, '-').toLowerCase()}-${session.date || 'today'}.png`;
    a.click();
    URL.revokeObjectURL(url);
  }, [getBlob, session]);

  const handleCopy = useCallback(async () => {
    const blob = await getBlob();
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error('Copy failed:', e);
      handleDownload();
    }
  }, [getBlob, handleDownload]);

  const handleShare = useCallback(async () => {
    const blob = await getBlob();
    if (!blob) return;
    const file = new File([blob], 'dragonfit-session.png', { type: 'image/png' });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `${session.day_name || 'Session'} — DragonFit`,
          text: t('session_share.share_text'),
        });
      } catch (e) {
        if (e.name !== 'AbortError') handleDownload();
      }
    } else {
      handleDownload();
    }
  }, [getBlob, session, handleDownload, t]);

  return (
    <div className="share-overlay" onClick={onClose}>
      <div style={{ display: 'contents' }} onClick={e => e.stopPropagation()}>

        <div className="share-close-row">
          <button className="share-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div
          className="share-preview-frame"
          style={{ width: PREVIEW_W, height: PREVIEW_H }}
        >
          {generating ? (
            <div className="share-preview-generating">
              <div className="loading-spinner" style={{ width: 32, height: 32 }} />
            </div>
          ) : imgUrl ? (
            <img
              className="share-preview-img"
              src={imgUrl}
              alt={t('session_share.preview_alt')}
              style={{ width: PREVIEW_W, height: PREVIEW_H }}
            />
          ) : (
            <div className="share-preview-generating" style={{ color: 'var(--muted-foreground)', fontSize: 13 }}>
              {t('session_share.error')}
            </div>
          )}
        </div>

        <div className="share-actions" style={{ maxWidth: PREVIEW_W + 40 }}>
          <button
            className="share-action-btn"
            onClick={handleDownload}
            disabled={generating}
          >
            <Download className="share-action-icon" />
            {t('session_share.save')}
          </button>

          <button
            className="share-action-btn"
            onClick={handleCopy}
            disabled={generating}
            style={copied ? { borderColor: 'var(--primary)', color: 'var(--primary)' } : {}}
          >
            {copied
              ? <Check className="share-action-icon" style={{ color: 'var(--primary)' }} />
              : <Copy className="share-action-icon" />
            }
            {copied ? t('session_share.copied') : t('session_share.copy')}
          </button>

          <button
            className="share-action-btn primary-action"
            onClick={handleShare}
            disabled={generating}
          >
            <Share2 className="share-action-icon" />
            {t('session_share.share')}
          </button>
        </div>

        <p className="share-hint">
          {t('session_share.format_hint')}
        </p>
      </div>
    </div>
  );
};

export default SessionShareModal;
